import { styled } from "@linaria/react";
import { t } from "@/themes/theme.ts";
import type { FC, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const TriggerWrapper = styled.div`
  display: inline-flex;
  cursor: pointer;
`;

const Panel = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 0.25rem;
  z-index: 1000;
  min-width: 10rem;
  background: white;
  border: 1px solid ${t.brandBorderColor};
  border-radius: ${t.brandBorderRadius};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: clip;
`;

export const DropdownItem = styled.div`
  display: block;
  padding: 0.5rem 1rem;
  font-size: var(--14px);
  color: ${t.brandTextPrimary};
  cursor: pointer;

  &:hover {
    background: #f1f5f9;
  }
`;

export const DropdownLink = styled(Link)`
  display: block;
  padding: 0.5rem 1rem;
  font-size: var(--14px);
  color: ${t.brandTextPrimary};
  text-decoration: none;

  transition: all 0.1s ease-in-out;

  &:hover {
    background: #f1f5f9;
  }
`;

interface DropdownProps {
  trigger: ReactNode;
  children: ReactNode;
}

export const Dropdown: FC<DropdownProps> = ({ trigger, children }) => {
  const [open, setOpen] = useState(false);
  const us = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClose = (e: MouseEvent) => {
      // amazing how in 2026, after all this time, we still need to ban comments and add two lines for something that would work in js
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      if (e.target && us.current && !us.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", onClose);
    return () => document.removeEventListener("click", onClose);
  }, [open]);

  return (
    <Wrapper>
      <TriggerWrapper
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((prev) => !prev);
          }
        }}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {trigger}
      </TriggerWrapper>
      {open && <Panel ref={us}>{children}</Panel>}
    </Wrapper>
  );
};
